import { useEffect, useMemo, useState } from "react";
import Delete from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { ok } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { mustBeDefined, mustParse } from "@thalamos/common";
import {
  ExtendedOrganisationArraySchema,
  UserDirectoryInstanceIdSchema,
  UserDirectoryKeysValues,
} from "@thalamos/vision-common";
import type {
  CreateUserDirectoryInstanceUnbranded,
  UpdateUserDirectoryInstanceUnbranded,
  UserDirectoryInstanceUnbranded,
  UserDirectoryKeys,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../../../api/index.js";
import { useToastNotifications } from "../../../../hooks/useToastNotifications.js";

export function UserDirectoryInstanceManagementPage() {
  const { userDirectoryInstanceId } = useParams<{
    userDirectoryInstanceId: string;
  }>();
  const { t } = useTranslation();
  const notifications = useToastNotifications();
  const [submitting, setSubmitting] = useState(false);

  const [{ data: orgData, loading: orgsLoading }] =
    apiHooks.organisation.search("", 1000);

  const [{ data: allInstances, loading: instancesLoading }] =
    apiHooks.userDirectoryInstance.getAll();

  const organisations = useMemo(
    () =>
      orgsLoading
        ? []
        : ok(orgData?.results)
            .andThen(mustBeDefined())
            .andThen(mustParse(ExtendedOrganisationArraySchema))
            .unwrapOr([]),
    [orgData?.results, orgsLoading],
  );

  const validatedId = UserDirectoryInstanceIdSchema.safeParse(
    userDirectoryInstanceId,
  );

  if (!validatedId.success) {
    throw new Error("Invalid user directory instance ID");
  }

  const existingInstance: UserDirectoryInstanceUnbranded | undefined = useMemo(
    () =>
      (allInstances ?? []).find(
        (inst: UserDirectoryInstanceUnbranded) =>
          inst.id === userDirectoryInstanceId,
      ),
    [allInstances, userDirectoryInstanceId],
  );

  const isEditMode = !!existingInstance;

  const [formState, setFormState] = useState<{
    tenantId: string;
    clientId: string;
    clientSecret: string;
    userDirectoryKey: UserDirectoryKeys | "";
    auth0ConnectionId: string;
    organisationId: string;
    supervisorMapping: string[];
    graphGroupMapping: { groupId: string; organisationId: string }[];
    commandUnitMapping: { organisationId: string; values: string[] }[];
    shoulderNumberMappingField: string;
    shoulderNumberValidationRegex: string;
    groupSyncDelayMinutes: string;
  }>({
    tenantId: "",
    clientId: "",
    clientSecret: "",
    userDirectoryKey: "",
    auth0ConnectionId: "",
    organisationId: "",
    supervisorMapping: [],
    graphGroupMapping: [],
    commandUnitMapping: [],
    shoulderNumberMappingField: "",
    shoulderNumberValidationRegex: "",
    groupSyncDelayMinutes: "",
  });

  useEffect(() => {
    if (existingInstance) {
      setFormState({
        tenantId: existingInstance.tenantId ?? "",
        clientId: existingInstance.clientId ?? "",
        clientSecret: existingInstance.clientSecret ?? "",
        userDirectoryKey: existingInstance.userDirectoryKey,
        auth0ConnectionId: existingInstance.auth0ConnectionId ?? "",
        organisationId: existingInstance.organisationId,
        supervisorMapping: existingInstance.supervisorMapping ?? [],
        graphGroupMapping: existingInstance.graphGroupMapping
          ? Object.entries(existingInstance.graphGroupMapping).map(
              ([groupId, orgId]) => ({
                groupId,
                organisationId: orgId,
              }),
            )
          : [],
        commandUnitMapping: existingInstance.commandUnitMapping
          ? Object.entries(existingInstance.commandUnitMapping).map(
              ([orgId, values]) => ({
                organisationId: orgId,
                values,
              }),
            )
          : [],
        shoulderNumberMappingField:
          existingInstance.shoulderNumberMappingField ?? "",
        shoulderNumberValidationRegex:
          existingInstance.shoulderNumberValidationRegex ?? "",
        groupSyncDelayMinutes:
          existingInstance.groupSyncDelayMinutes !== undefined
            ? String(existingInstance.groupSyncDelayMinutes)
            : "",
      });
    }
  }, [existingInstance]);

  const usedCommandUnitOrgIds = new Set(
    formState.commandUnitMapping.map((m) => m.organisationId),
  );

  const duplicateGraphGroupIds = useMemo(() => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const m of formState.graphGroupMapping) {
      const trimmed = m.groupId.trim();
      if (trimmed === "") continue;
      if (seen.has(trimmed)) {
        duplicates.add(trimmed);
      }
      seen.add(trimmed);
    }
    return duplicates;
  }, [formState.graphGroupMapping]);

  const hasDuplicateGraphGroupIds = duplicateGraphGroupIds.size > 0;

  const isAuth0Connection = formState.auth0ConnectionId.trim() !== "";
  const hasGraphApiCredentials =
    formState.tenantId.trim() !== "" &&
    formState.clientId.trim() !== "" &&
    formState.clientSecret.trim() !== "";

  const hasGraphGroupMapping =
    !isAuth0Connection &&
    formState.graphGroupMapping.some(
      (m) => m.groupId.trim() !== "" && m.organisationId.trim() !== "",
    );

  const groupSyncDelayMinutesValue = formState.groupSyncDelayMinutes.trim();
  const parsedGroupSyncDelay =
    groupSyncDelayMinutesValue !== ""
      ? Number(groupSyncDelayMinutesValue)
      : NaN;
  const hasValidGroupSyncDelay =
    Number.isInteger(parsedGroupSyncDelay) && parsedGroupSyncDelay > 0;
  const groupSyncDelayMissingError =
    hasGraphGroupMapping && groupSyncDelayMinutesValue === "";
  const groupSyncDelayInvalidError =
    groupSyncDelayMinutesValue !== "" && !hasValidGroupSyncDelay;

  const isValid =
    formState.userDirectoryKey.trim() !== "" &&
    formState.organisationId.trim() !== "" &&
    (isAuth0Connection || hasGraphApiCredentials) &&
    !hasDuplicateGraphGroupIds &&
    !groupSyncDelayMissingError &&
    !groupSyncDelayInvalidError;

  if (instancesLoading || orgsLoading) {
    return <CircularProgress />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        maxWidth: "600px",
        padding: "2rem",
      }}
    >
      <Typography variant="h4" component="div">
        {isEditMode
          ? t("admin.userDirectoryInstances.updateInstance")
          : t("admin.userDirectoryInstances.createInstance")}
      </Typography>
      <FormControl fullWidth required>
        <InputLabel id="organisation-select-label">
          {t("admin.userDirectoryInstances.organisation")}
        </InputLabel>
        <Select
          labelId="organisation-select-label"
          value={formState.organisationId}
          label={t("admin.userDirectoryInstances.organisation")}
          onChange={(e) => {
            setFormState({ ...formState, organisationId: e.target.value });
          }}
        >
          {[...organisations]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <TextField
        label={t("admin.userDirectoryInstances.auth0ConnectionId")}
        value={formState.auth0ConnectionId}
        onChange={(e) => {
          const newValue = e.target.value;
          setFormState({
            ...formState,
            auth0ConnectionId: newValue,
            // Graph group mapping is incompatible with Auth0 connections;
            // clear it when switching to Auth0 so hidden entries are not
            // submitted and rejected by the server.
            ...(newValue.trim() !== "" &&
              formState.graphGroupMapping.length > 0 && {
                graphGroupMapping: [],
              }),
          });
        }}
        helperText={t(
          "admin.userDirectoryInstances.auth0ConnectionIdHelperText",
        )}
      />
      <TextField
        required={!isAuth0Connection}
        label={t("admin.userDirectoryInstances.tenantId")}
        value={formState.tenantId}
        onChange={(e) => {
          setFormState({ ...formState, tenantId: e.target.value });
        }}
        helperText={
          isAuth0Connection
            ? t("admin.userDirectoryInstances.notRequiredForAuth0")
            : undefined
        }
      />
      <TextField
        required={!isAuth0Connection}
        label={t("admin.userDirectoryInstances.clientId")}
        value={formState.clientId}
        onChange={(e) => {
          setFormState({ ...formState, clientId: e.target.value });
        }}
        helperText={
          isAuth0Connection
            ? t("admin.userDirectoryInstances.notRequiredForAuth0")
            : undefined
        }
      />
      <TextField
        required={!isAuth0Connection}
        label={t("admin.userDirectoryInstances.clientSecret")}
        value={formState.clientSecret}
        onChange={(e) => {
          setFormState({ ...formState, clientSecret: e.target.value });
        }}
        helperText={
          isAuth0Connection
            ? t("admin.userDirectoryInstances.notRequiredForAuth0")
            : undefined
        }
      />
      <FormControl fullWidth required>
        <InputLabel id="user-directory-key-label">
          {t("admin.userDirectoryInstances.userDirectoryKey")}
        </InputLabel>
        <Select
          labelId="user-directory-key-label"
          value={formState.userDirectoryKey}
          label={t("admin.userDirectoryInstances.userDirectoryKey")}
          onChange={(e) => {
            setFormState({
              ...formState,
              userDirectoryKey: e.target.value,
            });
          }}
        >
          {UserDirectoryKeysValues.map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={formState.supervisorMapping}
        onChange={(_e, newValue) => {
          setFormState({ ...formState, supervisorMapping: newValue });
        }}
        renderValue={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip label={option} {...getTagProps({ index })} key={option} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("admin.userDirectoryInstances.supervisorMapping")}
          />
        )}
      />
      <TextField
        label={t("admin.userDirectoryInstances.shoulderNumberMappingField")}
        value={formState.shoulderNumberMappingField}
        onChange={(e) => {
          setFormState({
            ...formState,
            shoulderNumberMappingField: e.target.value,
          });
        }}
        helperText={t(
          "admin.userDirectoryInstances.shoulderNumberMappingFieldHelperText",
        )}
      />
      <TextField
        label={t("admin.userDirectoryInstances.shoulderNumberValidationRegex")}
        value={formState.shoulderNumberValidationRegex}
        onChange={(e) => {
          setFormState({
            ...formState,
            shoulderNumberValidationRegex: e.target.value,
          });
        }}
        helperText={t(
          "admin.userDirectoryInstances.shoulderNumberValidationRegexHelperText",
        )}
      />
      {!isAuth0Connection && (
        <>
          <Typography variant="h6" component="div">
            {t("admin.userDirectoryInstances.graphGroupMapping")}
          </Typography>
          {formState.graphGroupMapping.map((mapping, index) => (
            <Box
              key={`graph-group-${String(index)}`}
              sx={{
                display: "flex",
                gap: "1rem",
                flexDirection: "column",
                padding: "1rem",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <TextField
                  fullWidth
                  label={t("admin.userDirectoryInstances.graphGroupId")}
                  value={mapping.groupId}
                  error={duplicateGraphGroupIds.has(mapping.groupId.trim())}
                  helperText={
                    duplicateGraphGroupIds.has(mapping.groupId.trim())
                      ? t("admin.userDirectoryInstances.duplicateGraphGroupId")
                      : undefined
                  }
                  onChange={(e) => {
                    setFormState({
                      ...formState,
                      graphGroupMapping: formState.graphGroupMapping.map(
                        (item, i) =>
                          i === index
                            ? { ...item, groupId: e.target.value }
                            : item,
                      ),
                    });
                  }}
                />
                <IconButton
                  color="error"
                  onClick={() => {
                    const updated = formState.graphGroupMapping.filter(
                      (_, i) => i !== index,
                    );
                    setFormState({
                      ...formState,
                      graphGroupMapping: updated,
                    });
                  }}
                >
                  <Delete />
                </IconButton>
              </Box>
              <FormControl fullWidth>
                <InputLabel id={`graph-group-org-label-${String(index)}`}>
                  {t("admin.userDirectoryInstances.commandUnit")}
                </InputLabel>
                <Select
                  labelId={`graph-group-org-label-${String(index)}`}
                  value={mapping.organisationId}
                  label={t("admin.userDirectoryInstances.commandUnit")}
                  onChange={(e) => {
                    setFormState({
                      ...formState,
                      graphGroupMapping: formState.graphGroupMapping.map(
                        (item, i) =>
                          i === index
                            ? { ...item, organisationId: e.target.value }
                            : item,
                      ),
                    });
                  }}
                >
                  {[...organisations]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          ))}
          <Button
            variant="outlined"
            onClick={() => {
              setFormState({
                ...formState,
                graphGroupMapping: [
                  ...formState.graphGroupMapping,
                  { groupId: "", organisationId: "" },
                ],
              });
            }}
          >
            {t("admin.userDirectoryInstances.addGraphGroupMapping")}
          </Button>
          <TextField
            label={t("admin.userDirectoryInstances.groupSyncDelayMinutes")}
            value={formState.groupSyncDelayMinutes}
            type="number"
            required={hasGraphGroupMapping}
            error={groupSyncDelayMissingError || groupSyncDelayInvalidError}
            helperText={
              groupSyncDelayMissingError
                ? t(
                    "admin.userDirectoryInstances.groupSyncDelayMinutesRequired",
                  )
                : groupSyncDelayInvalidError
                  ? t(
                      "admin.userDirectoryInstances.groupSyncDelayMinutesInvalid",
                    )
                  : t(
                      "admin.userDirectoryInstances.groupSyncDelayMinutesHelperText",
                    )
            }
            onChange={(e) => {
              setFormState({
                ...formState,
                groupSyncDelayMinutes: e.target.value,
              });
            }}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        </>
      )}
      <Typography variant="h6" component="div">
        {t("admin.userDirectoryInstances.commandUnitMapping")}
      </Typography>{" "}
      {formState.commandUnitMapping.map((mapping, index) => (
        <Box
          key={`${mapping.organisationId}-${String(index)}`}
          sx={{
            display: "flex",
            gap: "1rem",
            flexDirection: "column",
            padding: "1rem",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FormControl fullWidth>
              <InputLabel id={`command-unit-org-label-${String(index)}`}>
                {t("admin.userDirectoryInstances.organisation")}
              </InputLabel>
              <Select
                labelId={`command-unit-org-label-${String(index)}`}
                value={mapping.organisationId}
                label={t("admin.userDirectoryInstances.organisation")}
                onChange={(e) => {
                  setFormState({
                    ...formState,
                    commandUnitMapping: formState.commandUnitMapping.map(
                      (item, i) =>
                        i === index
                          ? { ...item, organisationId: e.target.value }
                          : item,
                    ),
                  });
                }}
              >
                {organisations
                  .filter(
                    (org) =>
                      org.id === mapping.organisationId ||
                      !usedCommandUnitOrgIds.has(org.id),
                  )
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <IconButton
              color="error"
              onClick={() => {
                const updated = formState.commandUnitMapping.filter(
                  (_, i) => i !== index,
                );
                setFormState({
                  ...formState,
                  commandUnitMapping: updated,
                });
              }}
            >
              <Delete />
            </IconButton>
          </Box>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={mapping.values}
            onChange={(_e, newValue) => {
              setFormState({
                ...formState,
                commandUnitMapping: formState.commandUnitMapping.map(
                  (item, i) =>
                    i === index ? { ...item, values: newValue } : item,
                ),
              });
            }}
            renderValue={(value, getTagProps) =>
              value.map((option, i) => (
                <Chip
                  label={option}
                  {...getTagProps({ index: i })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.userDirectoryInstances.commandUnitNames")}
              />
            )}
          />
        </Box>
      ))}
      <Button
        variant="outlined"
        onClick={() => {
          setFormState({
            ...formState,
            commandUnitMapping: [
              ...formState.commandUnitMapping,
              { organisationId: "", values: [] },
            ],
          });
        }}
      >
        {t("admin.userDirectoryInstances.addCommandUnitMapping")}
      </Button>
      <Button
        disabled={!isValid || submitting}
        variant="contained"
        onClick={() => {
          setSubmitting(true);
          const body: CreateUserDirectoryInstanceUnbranded = {
            organisationId: formState.organisationId,
            userDirectoryKey: formState.userDirectoryKey as UserDirectoryKeys,
            ...(formState.tenantId.trim() !== "" && {
              tenantId: formState.tenantId,
            }),
            ...(formState.clientId.trim() !== "" && {
              clientId: formState.clientId,
            }),
            ...(formState.clientSecret.trim() !== "" && {
              clientSecret: formState.clientSecret,
            }),
            ...(formState.auth0ConnectionId.trim() !== "" && {
              auth0ConnectionId: formState.auth0ConnectionId,
            }),
            ...(formState.supervisorMapping.length > 0 && {
              supervisorMapping: formState.supervisorMapping,
            }),
            ...(!isAuth0Connection &&
              formState.graphGroupMapping.length > 0 && {
                graphGroupMapping: Object.fromEntries(
                  formState.graphGroupMapping
                    .filter(
                      (m) =>
                        m.groupId.trim() !== "" &&
                        m.organisationId.trim() !== "",
                    )
                    .map((m) => [m.groupId, m.organisationId]),
                ),
              }),
            ...(formState.commandUnitMapping.length > 0 && {
              commandUnitMapping: Object.fromEntries(
                formState.commandUnitMapping
                  .filter((m) => m.organisationId !== "" && m.values.length > 0)
                  .map((m) => [m.organisationId, m.values]),
              ),
            }),
            ...(formState.shoulderNumberMappingField.trim() !== "" && {
              shoulderNumberMappingField: formState.shoulderNumberMappingField,
            }),
            ...(formState.shoulderNumberValidationRegex.trim() !== "" && {
              shoulderNumberValidationRegex:
                formState.shoulderNumberValidationRegex,
            }),
            ...(hasValidGroupSyncDelay && {
              groupSyncDelayMinutes: parsedGroupSyncDelay,
            }),
          };

          if (isEditMode) {
            const updateBody: UpdateUserDirectoryInstanceUnbranded = {
              organisationId: formState.organisationId,
              userDirectoryKey: formState.userDirectoryKey as UserDirectoryKeys,
              ...(formState.tenantId.trim() !== "" && {
                tenantId: formState.tenantId,
              }),
              ...(formState.clientId.trim() !== "" &&
                formState.clientId !== existingInstance.clientId && {
                  clientId: formState.clientId,
                }),
              ...(formState.clientSecret.trim() !== "" &&
                formState.clientSecret !== existingInstance.clientSecret && {
                  clientSecret: formState.clientSecret,
                }),
              auth0ConnectionId:
                formState.auth0ConnectionId.trim() || undefined,
              supervisorMapping: formState.supervisorMapping,
              graphGroupMapping: isAuth0Connection
                ? {}
                : Object.fromEntries(
                    formState.graphGroupMapping
                      .filter(
                        (m) =>
                          m.groupId.trim() !== "" &&
                          m.organisationId.trim() !== "",
                      )
                      .map((m) => [m.groupId, m.organisationId]),
                  ),
              commandUnitMapping: Object.fromEntries(
                formState.commandUnitMapping
                  .filter((m) => m.organisationId !== "" && m.values.length > 0)
                  .map((m) => [m.organisationId, m.values]),
              ),
              shoulderNumberMappingField:
                formState.shoulderNumberMappingField.trim() || undefined,
              shoulderNumberValidationRegex:
                formState.shoulderNumberValidationRegex.trim() || undefined,
              groupSyncDelayMinutes: hasValidGroupSyncDelay
                ? parsedGroupSyncDelay
                : formState.groupSyncDelayMinutes.trim() === ""
                  ? null
                  : undefined,
            };
            api.userDirectoryInstance
              .update(validatedId.data, updateBody)
              .then((response) => {
                if (response.status === 204) {
                  notifications.show(
                    t("admin.userDirectoryInstances.updateSuccess"),
                    { severity: "success" },
                  );
                } else {
                  notifications.show(
                    t("admin.userDirectoryInstances.updateError"),
                    { severity: "error" },
                  );
                }
              })
              .catch((_error: unknown) => {
                notifications.show(
                  t("admin.userDirectoryInstances.updateError"),
                  { severity: "error" },
                );
              })
              .finally(() => {
                setSubmitting(false);
              });
          } else {
            api.userDirectoryInstance
              .create(validatedId.data, body)
              .then((response) => {
                if (response.status === 204) {
                  notifications.show(
                    t("admin.userDirectoryInstances.createSuccess"),
                    { severity: "success" },
                  );
                } else {
                  notifications.show(
                    t("admin.userDirectoryInstances.createError"),
                    { severity: "error" },
                  );
                }
              })
              .catch((_error: unknown) => {
                notifications.show(
                  t("admin.userDirectoryInstances.createError"),
                  { severity: "error" },
                );
              })
              .finally(() => {
                setSubmitting(false);
              });
          }
        }}
      >
        {isEditMode
          ? t("admin.userDirectoryInstances.update")
          : t("admin.userDirectoryInstances.create")}
      </Button>
    </Box>
  );
}
